import { createFileRoute } from '@tanstack/react-router'
import { Plus, Briefcase, Users, Trash2, Edit } from 'lucide-react'
import { useState } from 'react'

import { PositionForm } from '@/components/hr/position/position-form'
import { useConfirmDialog } from '@/hooks'
import {
  usePositionList,
  useDeletePosition,
  type PositionWithDetails,
} from '@/hooks'

export const Route = createFileRoute('/_authenticated/hr/positions/')({
  component: PositionListPage,
})

function PositionListPage() {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingPosition, setEditingPosition] = useState<PositionWithDetails | null>(null)

  const { data: positions, isLoading, error } = usePositionList()
  const deleteMutation = useDeletePosition()
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

  const handleDelete = async (id: string, name: string) => {
    const confirmed = await confirmWithPreset(
      'delete',
      `确定要删除职位"${name}"吗？`
    )
    if (confirmed) {
      setLoading(true)
      try {
        await deleteMutation.mutateAsync(id)
      } finally {
        setLoading(false)
      }
    }
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">职位管理</h1>
          <p className="text-muted-foreground">管理公司职位信息</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          新建职位
        </button>
      </div>

      {showCreateForm && (
        <PositionForm onClose={() => setShowCreateForm(false)} onSuccess={() => setShowCreateForm(false)} />
      )}

      {editingPosition && (
        <PositionForm
          position={editingPosition}
          onClose={() => setEditingPosition(null)}
          onSuccess={() => setEditingPosition(null)}
        />
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : !positions || positions.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <Briefcase className="h-12 w-12 text-muted-foreground" />
          <div className="text-center">
            <p className="text-lg font-medium">暂无职位</p>
            <p className="text-sm text-muted-foreground">点击上方按钮创建第一个职位</p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-medium">职位名称</th>
                <th className="px-4 py-3 text-left text-sm font-medium">编码</th>
                <th className="px-4 py-3 text-left text-sm font-medium">所属部门</th>
                <th className="px-4 py-3 text-left text-sm font-medium">级别</th>
                <th className="px-4 py-3 text-left text-sm font-medium">员工数</th>
                <th className="px-4 py-3 text-left text-sm font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((pos) => (
                <tr key={pos.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium">{pos.name}</td>
                  <td className="px-4 py-3 text-sm font-mono text-muted-foreground">{pos.code}</td>
                  <td className="px-4 py-3 text-sm">{pos.department?.name || '-'}</td>
                  <td className="px-4 py-3 text-sm">{pos.level ?? '-'}</td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Users className="h-3.5 w-3.5" />
                      {pos.employeeCount ?? 0}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditingPosition(pos)}
                        className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => void handleDelete(pos.id, pos.name)}
                        className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDialog />
    </div>
  )
}
